import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, Trash2, Edit2, Save, X, ChevronUp, ChevronDown, Eye, EyeOff,
  Calendar, AlertCircle, Info, CheckCircle, AlertTriangle, MessageSquare,
  Star, Users, Code, Award, Box, Image, Video, Upload, Clock, MapPin, Train
} from 'lucide-react';
import MediaUploader from '../components/MediaUploader';

// ✅ WICHTIG: Kein Port :443 verwenden! Verursacht Probleme bei Image-Loading
const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch';

const EarlyAccessContentManagement = ({ token }) => {
  // State für Content-Daten
  const [contentData, setContentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // State für verschiedene Sections
  const [welcomeMessages, setWelcomeMessages] = useState([]);
  const [features, setFeatures] = useState([]);
  const [news, setNews] = useState([]);
  
  // Camp Info State
  const [campInfo, setCampInfo] = useState({
    title: '',
    location: '',
    description: '',
    theme: '',
    capacity: 0,
    headerImageUrl: '',
    invitationImageUrl: '',
    startDate: '',
    endDate: '',
    trainNotes: '',
    sbbLink: ''
  });
  
  // Train Schedule State
  const [trainSchedule, setTrainSchedule] = useState({
    outbound: {
      date: '',
      departureStation: '',
      departureTime: '',
      arrivalStation: '',
      arrivalTime: '',
      stops: []
    },
    return: {
      date: '',
      departureStation: '',
      departureTime: '',
      arrivalStation: '',
      arrivalTime: '',
      stops: []
    },
    additionalInfo: ''
  });
  
  const [trainChanges, setTrainChanges] = useState({
    hasChanges: false,
    changeDate: '',
    transportType: 'train',
    oldSchedule: {
      departure: { station: '', time: '' },
      arrival: { station: '', time: '' },
      duration: ''
    },
    newSchedule: {
      stops: [],
      totalDuration: ''
    },
    importantNotes: []
  });
  const [supervisors, setSupervisors] = useState([]);
  const [developmentTeam, setDevelopmentTeam] = useState([]);
  const [specialThanks, setSpecialThanks] = useState([]);
  const [eventDates, setEventDates] = useState({
    startDate: '',
    endDate: ''
  });
  const [contentBoxes, setContentBoxes] = useState([]);
  
  // UI State
  const [activeTab, setActiveTab] = useState('welcome');
  const [editingItem, setEditingItem] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    welcome: true,
    features: false,
    news: false,
    train: false,
    team: false,
    dates: false,
    boxes: false
  });

  // Fetch Content beim Laden
  useEffect(() => {
    fetchContent();
  }, []);

  // 🆕 URL-Bereinigung: Entfernt :443 Port und stellt sicher, dass URL korrekt ist
  const cleanImageUrl = (url) => {
    if (!url) return '';
    
    // Entferne :443 aus der URL (verursacht Probleme bei mobilen Apps)
    let cleanUrl = url.replace(':443/', '/').replace(':443', '');
    
    console.log('🧹 URL cleaned:', url, '→', cleanUrl);
    return cleanUrl;
  };

  // 🔧 FIX: Konvertiere /uploads/early-access/ URLs zu /api/early-access/serve-image/
  // Nginx blockiert direkten Zugriff auf /uploads/, daher müssen wir den Spring Boot Endpoint verwenden
  // 👁️ WICHTIG: Diese Funktion ist NUR für die ANZEIGE (Preview im <img> Tag)
  const getDisplayUrl = (url) => {
    if (!url) return '';
    
    // Entferne zuerst :443 falls vorhanden
    let cleanUrl = url.replace(':443/', '/').replace(':443', '');
    
    // Wenn es bereits eine serve-image URL ist, nicht ändern
    if (cleanUrl.includes('/api/early-access/serve-image/')) {
      return cleanUrl;
    }
    
    // Konvertiere /uploads/early-access/filename.jpg zu /api/early-access/serve-image/filename.jpg
    // NUR FÜR DISPLAY - nicht für Speicherung!
    if (cleanUrl.includes('/uploads/early-access/')) {
      const filename = cleanUrl.split('/uploads/early-access/').pop();
      const newUrl = `${BASE_URL}/api/early-access/serve-image/${filename}`;
      console.log(`👁️ Display URL: ${url} → ${newUrl}`);
      return newUrl;
    }
    
    return cleanUrl;
  };

  // 💾 Neue Funktion: Bereitet URL für SPEICHERUNG vor
  // Konvertiert serve-image URLs zurück zu /uploads/ URLs und entfernt :443
  const getStorageUrl = (url) => {
    if (!url) return '';
    
    // Entferne :443
    let cleanUrl = url.replace(':443/', '/').replace(':443', '');
    
    // 🔧 FIX: Konvertiere /api/early-access/serve-image/ zurück zu /uploads/early-access/
    // Das ist wichtig für alte Daten, die noch die konvertierte URL haben!
    if (cleanUrl.includes('/api/early-access/serve-image/')) {
      const filename = cleanUrl.split('/api/early-access/serve-image/').pop();
      // Entferne Query-Parameter falls vorhanden (z.B. ?_t=123)
      const cleanFilename = filename.split('?')[0];
      cleanUrl = `${BASE_URL}/uploads/early-access/${cleanFilename}`;
      console.log(`🔄 Storage URL (converted back): ${url} → ${cleanUrl}`);
    } else if (cleanUrl.includes('/uploads/early-access/')) {
      // URL ist bereits im richtigen Format - nichts tun
      console.log(`💾 Storage URL (already correct): ${cleanUrl}`);
    } else if (cleanUrl) {
      console.log(`⚠️ Storage URL (unknown format, keeping): ${cleanUrl}`);
    }
    
    return cleanUrl;
  };

  const fetchContent = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔄 Fetching content from backend...');
      const response = await axios.get(`${BASE_URL}/api/early-access/content`);
      const data = response.data;
      
      console.log('📥 Received data from backend:', data);
      
      setContentData(data);
      setWelcomeMessages(data.welcomeMessages || []);
      setFeatures(data.features || []);
      
      // 🔧 FIX: Konvertiere serve-image URLs zurück zu /uploads/ URLs beim Laden
      // Das stellt sicher, dass wir immer mit den originalen URLs arbeiten
      const cleanedNews = (data.news || []).map(item => ({
        ...item,
        imageUrl: item.imageUrl ? getStorageUrl(item.imageUrl) : null,
        mediaUrl: item.mediaUrl ? getStorageUrl(item.mediaUrl) : null
      }));
      console.log('📥 News URLs cleaned for storage format:', cleanedNews.map(n => ({ id: n.id, imageUrl: n.imageUrl })));
      setNews(cleanedNews);
      
      // Camp Info laden - mit URL-Bereinigung
      const loadedCampInfo = data.campInfo || {
        title: '',
        location: '',
        description: '',
        theme: '',
        capacity: 0,
        headerImageUrl: '',
        invitationImageUrl: '',
        startDate: '',
        endDate: '',
        trainNotes: '',
        sbbLink: ''
      };
      // Bereinige Camp Info URLs
      if (loadedCampInfo.headerImageUrl) {
        loadedCampInfo.headerImageUrl = getStorageUrl(loadedCampInfo.headerImageUrl);
      }
      if (loadedCampInfo.invitationImageUrl) {
        loadedCampInfo.invitationImageUrl = getStorageUrl(loadedCampInfo.invitationImageUrl);
      }
      console.log('📥 Camp Info URLs cleaned:', { header: loadedCampInfo.headerImageUrl, invitation: loadedCampInfo.invitationImageUrl });
      setCampInfo(loadedCampInfo);
      
      // Train Schedule laden - mit Fallback für stops
      setTrainSchedule(data.trainSchedule || {
        outbound: { stops: [], departureStation: '', arrivalStation: '', departureTime: '', arrivalTime: '', date: '' },
        return: { stops: [], departureStation: '', arrivalStation: '', departureTime: '', arrivalTime: '', date: '' },
        additionalInfo: ''
      });
      
      setTrainChanges(data.trainChanges || {
        hasChanges: false,
        changeDate: '',
        transportType: 'train',
        oldSchedule: {
          departure: { station: '', time: '' },
          arrival: { station: '', time: '' },
          duration: ''
        },
        newSchedule: {
          stops: [],
          totalDuration: ''
        },
        importantNotes: []
      });
      
      // 🔧 FIX: Team Member URLs beim Laden bereinigen
      const cleanedSupervisors = (data.supervisors || []).map(member => ({
        ...member,
        imageUrl: member.imageUrl ? getStorageUrl(member.imageUrl) : ''
      }));
      setSupervisors(cleanedSupervisors);
      
      const cleanedDevelopmentTeam = (data.developmentTeam || []).map(member => ({
        ...member,
        imageUrl: member.imageUrl ? getStorageUrl(member.imageUrl) : ''
      }));
      setDevelopmentTeam(cleanedDevelopmentTeam);
      
      const cleanedSpecialThanks = (data.specialThanks || []).map(member => ({
        ...member,
        imageUrl: member.imageUrl ? getStorageUrl(member.imageUrl) : ''
      }));
      setSpecialThanks(cleanedSpecialThanks);
      
      setEventDates(data.eventDates || { startDate: '', endDate: '' });
      
      // Content Boxes laden - stelle sicher, dass boxType korrekt gemappt wird
      const loadedBoxes = (data.contentBoxes || []).map(box => ({
        ...box,
        // Fallback: wenn boxType nicht vorhanden, nutze type oder default 'info'
        boxType: box.boxType || box.type || 'info'
      }));
      setContentBoxes(loadedBoxes);
      
      console.log('✅ Content loaded successfully');
      setLoading(false);
    } catch (err) {
      console.error('❌ Error fetching content:', err);
      console.error('📋 Error details:', err.response?.data);
      
      // 🆕 FALLBACK: LocalStorage laden wenn Backend fehlschlägt
      const backup = localStorage.getItem('earlyAccessContentBackup');
      const backupDate = localStorage.getItem('earlyAccessContentBackupDate');
      
      if (backup) {
        console.log('💾 Loading from localStorage backup (created:', backupDate, ')');
        try {
          const data = JSON.parse(backup);
          
          setWelcomeMessages(data.welcomeMessages || []);
          setFeatures(data.features || []);
          setNews(data.news || []);
          
          setCampInfo(data.campInfo || {
            title: '',
            location: '',
            description: '',
            theme: '',
            capacity: 0,
            headerImageUrl: '',
            invitationImageUrl: '',
            startDate: '',
            endDate: '',
            trainNotes: '',
            sbbLink: ''
          });
          
          setTrainSchedule(data.trainSchedule || {
            outbound: { stops: [], departureStation: '', arrivalStation: '', departureTime: '', arrivalTime: '', date: '' },
            return: { stops: [], departureStation: '', arrivalStation: '', departureTime: '', arrivalTime: '', date: '' },
            additionalInfo: ''
          });
          
          setTrainChanges(data.trainChanges || {
            hasChanges: false,
            changeDate: '',
            transportType: 'train',
            oldSchedule: {
              departure: { station: '', time: '' },
              arrival: { station: '', time: '' },
              duration: ''
            },
            newSchedule: {
              stops: [],
              totalDuration: ''
            },
            importantNotes: []
          });
          
          setSupervisors(data.supervisors || []);
          setDevelopmentTeam(data.developmentTeam || []);
          setSpecialThanks(data.specialThanks || []);
          setEventDates(data.eventDates || { startDate: '', endDate: '' });
          
          // Content Boxes laden - stelle sicher, dass boxType korrekt gemappt wird
          const loadedBoxes = (data.contentBoxes || []).map(box => ({
            ...box,
            boxType: box.boxType || box.type || 'info'
          }));
          setContentBoxes(loadedBoxes);
          
          setError(`⚠️ Backend unavailable - Loaded from local backup (${backupDate ? new Date(backupDate).toLocaleString() : 'unknown date'})`);
          console.log('✅ Content loaded from localStorage backup');
        } catch (parseError) {
          console.error('❌ Error parsing localStorage backup:', parseError);
          setError('Failed to load content. Starting with empty data.');
        }
      } else {
        console.log('📦 No backup found in localStorage');
        setError('Failed to load content. Starting with empty data.');
      }
      
      setLoading(false);
    }
  };

  const saveContent = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage('');
    
    try {
      // 🔧 FIX: Konvertiere alle serve-image URLs zurück zu /uploads/ URLs vor dem Speichern
      // Das verhindert die doppelte Konvertierung!
      
      // News URLs konvertieren
      const cleanedNews = news.map(item => ({
        ...item,
        imageUrl: item.imageUrl ? getStorageUrl(item.imageUrl) : null,
        mediaUrl: item.mediaUrl ? getStorageUrl(item.mediaUrl) : null
      }));
      
      // Camp Info URLs konvertieren
      const cleanedCampInfo = {
        ...campInfo,
        headerImageUrl: campInfo.headerImageUrl ? getStorageUrl(campInfo.headerImageUrl) : '',
        invitationImageUrl: campInfo.invitationImageUrl ? getStorageUrl(campInfo.invitationImageUrl) : ''
      };
      
      // Team Member URLs konvertieren
      const cleanedSupervisors = supervisors.map(member => ({
        ...member,
        imageUrl: member.imageUrl ? getStorageUrl(member.imageUrl) : ''
      }));
      
      const cleanedDevelopmentTeam = developmentTeam.map(member => ({
        ...member,
        imageUrl: member.imageUrl ? getStorageUrl(member.imageUrl) : ''
      }));
      
      const cleanedSpecialThanks = specialThanks.map(member => ({
        ...member,
        imageUrl: member.imageUrl ? getStorageUrl(member.imageUrl) : ''
      }));
      
      // Payload vorbereiten mit BEREINIGTEN URLs
      const payload = {
        welcomeMessages,
        features,
        news: cleanedNews,
        campInfo: cleanedCampInfo,
        trainSchedule,
        trainChanges,
        supervisors: cleanedSupervisors,
        developmentTeam: cleanedDevelopmentTeam,
        specialThanks: cleanedSpecialThanks,
        eventDates,
        contentBoxes
      };

      // 🔍 DEBUG: Zeige die konvertierten URLs
      console.log('🔍 DEBUG - Original News URLs:', news.map(n => ({ id: n.id, imageUrl: n.imageUrl })));
      console.log('🔍 DEBUG - Cleaned News URLs:', cleanedNews.map(n => ({ id: n.id, imageUrl: n.imageUrl })));
      console.log('🔍 DEBUG - Original Camp Info URLs:', { header: campInfo.headerImageUrl, invitation: campInfo.invitationImageUrl });
      console.log('🔍 DEBUG - Cleaned Camp Info URLs:', { header: cleanedCampInfo.headerImageUrl, invitation: cleanedCampInfo.invitationImageUrl });
      
      console.log('📤 Sending payload:', JSON.stringify(payload, null, 2));

      const response = await axios.post(
        `${BASE_URL}/api/early-access/content`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('✅ Save successful:', response.data);
      
      // 🆕 ZUSÄTZLICH: LocalStorage Backup speichern
      localStorage.setItem('earlyAccessContentBackup', JSON.stringify(payload));
      localStorage.setItem('earlyAccessContentBackupDate', new Date().toISOString());
      console.log('💾 Backup saved to localStorage');
      
      setSuccessMessage('Content successfully saved!');
      
      // Reload content from backend to verify save
      console.log('🔄 Reloading content to verify save...');
      await fetchContent();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('❌ Error saving content:', err);
      console.error('📋 Response status:', err.response?.status);
      console.error('📋 Response data:', err.response?.data);
      console.error('📋 Response headers:', err.response?.headers);
      
      // Detaillierte Fehlermeldung
      let errorMessage = 'Failed to save content';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else {
          errorMessage = `Server Error: ${JSON.stringify(errorData)}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Toggle Section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Welcome Messages Functions
  const addWelcomeMessage = () => {
    setWelcomeMessages([...welcomeMessages, '']);
  };

  const updateWelcomeMessage = (index, value) => {
    const updated = [...welcomeMessages];
    updated[index] = value;
    setWelcomeMessages(updated);
  };

  const removeWelcomeMessage = (index) => {
    setWelcomeMessages(welcomeMessages.filter((_, i) => i !== index));
  };

  // Feature Functions
  const addFeature = () => {
    setFeatures([...features, {
      id: Date.now().toString(),
      title: '',
      description: '',
      icon: 'calendar',
      displayOrder: features.length,
      isActive: true
    }]);
  };

  const updateFeature = (index, field, value) => {
    const updated = [...features];
    updated[index][field] = value;
    setFeatures(updated);
  };

  const removeFeature = (index) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const moveFeature = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === features.length - 1)) return;
    
    const updated = [...features];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    
    // Update displayOrder
    updated.forEach((item, idx) => {
      item.displayOrder = idx;
    });
    
    setFeatures(updated);
  };

  // News Functions
  const addNews = () => {
    const newId = `news-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.log('📰 Adding new news item with ID:', newId);
    setNews([...news, {
      id: newId,
      title: '',
      subtitle: '',
      imageUrl: '',      // ✅ Geändert von mediaUrl zu imageUrl (konsistent mit MediaUploader)
      mediaType: 'image',
      newsDate: new Date().toISOString(),
      date: new Date().toISOString(),
      scrollTarget: '',
      displayOrder: news.length,
      isActive: true
    }]);
  };

  const updateNews = (index, field, value) => {
    console.log(`📰 Updating news ${index}: ${field} = ${value}`);
    setNews(prevNews => {
      const updated = [...prevNews];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      console.log(`📰 News ${index} after update:`, JSON.stringify(updated[index], null, 2));
      return updated;
    });
  };

  const removeNews = (index) => {
    setNews(news.filter((_, i) => i !== index));
  };

  const moveNews = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === news.length - 1)) return;
    
    const updated = [...news];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    
    updated.forEach((item, idx) => {
      item.displayOrder = idx;
    });
    
    setNews(updated);
  };

  // Team Member Functions (works for supervisors, developers, special thanks)
  const addTeamMember = (type) => {
    const member = {
      id: Date.now().toString(),
      name: '',
      title: '',
      imageUrl: '',
      phone: '',
      email: '',
      memberType: type,
      displayOrder: 0,
      isActive: true
    };

    switch(type) {
      case 'supervisor':
        setSupervisors([...supervisors, member]);
        break;
      case 'developer':
        setDevelopmentTeam([...developmentTeam, member]);
        break;
      case 'special_thanks':
        setSpecialThanks([...specialThanks, member]);
        break;
      default:
        break;
    }
  };

  const updateTeamMember = (type, index, field, value) => {
    let updated;
    switch(type) {
      case 'supervisor':
        updated = [...supervisors];
        updated[index][field] = value;
        setSupervisors(updated);
        break;
      case 'developer':
        updated = [...developmentTeam];
        updated[index][field] = value;
        setDevelopmentTeam(updated);
        break;
      case 'special_thanks':
        updated = [...specialThanks];
        updated[index][field] = value;
        setSpecialThanks(updated);
        break;
      default:
        break;
    }
  };

  const removeTeamMember = (type, index) => {
    switch(type) {
      case 'supervisor':
        setSupervisors(supervisors.filter((_, i) => i !== index));
        break;
      case 'developer':
        setDevelopmentTeam(developmentTeam.filter((_, i) => i !== index));
        break;
      case 'special_thanks':
        setSpecialThanks(specialThanks.filter((_, i) => i !== index));
        break;
      default:
        break;
    }
  };

  // Content Box Functions
  const addContentBox = () => {
    setContentBoxes([...contentBoxes, {
      id: Date.now().toString(),
      type: 'info',      // ✅ Beide Felder setzen für Kompatibilität
      boxType: 'info',   // ✅ Frontend verwendet boxType
      title: '',
      content: '',
      icon: 'info',
      displayOrder: contentBoxes.length,
      isActive: true
    }]);
  };

  const updateContentBox = (index, field, value) => {
    console.log(`📦 Updating content box ${index}: ${field} = ${value}`);
    const updated = [...contentBoxes];
    
    // ✅ Wenn type oder boxType geändert wird, beide Felder synchronisieren
    if (field === 'type' || field === 'boxType') {
      updated[index] = {
        ...updated[index],
        type: value,
        boxType: value
      };
    } else {
      updated[index] = {
        ...updated[index],
        [field]: value
      };
    }
    
    setContentBoxes(updated);
    console.log('📦 Updated content boxes:', updated);
  };

  const removeContentBox = (index) => {
    setContentBoxes(contentBoxes.filter((_, i) => i !== index));
  };

  const moveContentBox = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === contentBoxes.length - 1)) return;
    
    const updated = [...contentBoxes];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    
    updated.forEach((item, idx) => {
      item.displayOrder = idx;
    });
    
    setContentBoxes(updated);
  };

  // Camp Info Functions
  const updateCampInfo = (field, value) => {
    setCampInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Train Schedule Functions
  const addTrainStop = (direction) => {
    setTrainSchedule(prev => ({
      ...prev,
      [direction]: {
        ...prev[direction],
        stops: [
          ...(prev[direction]?.stops || []),
          {
            id: Date.now().toString(),
            station: '',
            time: '',
            platform: '',
            type: 'stop',
            train: '',
            description: ''
          }
        ]
      }
    }));
  };

  const updateTrainStop = (direction, index, field, value) => {
    setTrainSchedule(prev => {
      const updatedStops = [...(prev[direction]?.stops || [])];
      updatedStops[index][field] = value;
      return {
        ...prev,
        [direction]: {
          ...prev[direction],
          stops: updatedStops
        }
      };
    });
  };

  const removeTrainStop = (direction, index) => {
    setTrainSchedule(prev => ({
      ...prev,
      [direction]: {
        ...prev[direction],
        stops: (prev[direction]?.stops || []).filter((_, i) => i !== index)
      }
    }));
  };

  const moveTrainStop = (direction, index, moveDirection) => {
    const stops = trainSchedule[direction]?.stops || [];
    if ((moveDirection === 'up' && index === 0) || (moveDirection === 'down' && index === stops.length - 1)) return;
    
    const updated = [...stops];
    const newIndex = moveDirection === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    
    setTrainSchedule(prev => ({
      ...prev,
      [direction]: {
        ...prev[direction],
        stops: updated
      }
    }));
  };

  // Train Changes Functions
  const updateTrainChanges = (field, value) => {
    setTrainChanges(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addImportantNote = () => {
    setTrainChanges(prev => ({
      ...prev,
      importantNotes: [...(prev.importantNotes || []), '']
    }));
  };

  const updateImportantNote = (index, value) => {
    const updated = [...trainChanges.importantNotes];
    updated[index] = value;
    setTrainChanges(prev => ({
      ...prev,
      importantNotes: updated
    }));
  };

  const removeImportantNote = (index) => {
    setTrainChanges(prev => ({
      ...prev,
      importantNotes: prev.importantNotes.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading Early Access Content...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Early Access Content Management</h1>
          <p style={styles.subtitle}>Manage all content for the Early Access Screen</p>
          {localStorage.getItem('earlyAccessContentBackup') && (
            <p style={styles.backupInfo}>
              💾 Local backup available: {new Date(localStorage.getItem('earlyAccessContentBackupDate') || Date.now()).toLocaleString()}
            </p>
          )}
        </div>
        <button 
          onClick={saveContent} 
          disabled={saving}
          style={{...styles.saveButton, opacity: saving ? 0.6 : 1}}
        >
          {saving ? 'Saving...' : 'Save All Changes'}
          <Save size={20} style={{marginLeft: '8px'}} />
        </button>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div style={styles.successAlert}>
          <CheckCircle size={20} />
          <span>{successMessage}</span>
        </div>
      )}
      
      {error && (
        <div style={styles.errorAlert}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={styles.tabContainer}>
        <button 
          onClick={() => setActiveTab('welcome')}
          style={{...styles.tab, ...(activeTab === 'welcome' ? styles.activeTab : {})}}
        >
          <MessageSquare size={18} />
          Welcome
        </button>
        <button 
          onClick={() => setActiveTab('features')}
          style={{...styles.tab, ...(activeTab === 'features' ? styles.activeTab : {})}}
        >
          <Star size={18} />
          Features
        </button>
        <button 
          onClick={() => setActiveTab('news')}
          style={{...styles.tab, ...(activeTab === 'news' ? styles.activeTab : {})}}
        >
          <Image size={18} />
          News
        </button>
        <button 
          onClick={() => setActiveTab('camp')}
          style={{...styles.tab, ...(activeTab === 'camp' ? styles.activeTab : {})}}
        >
          <MapPin size={18} />
          Camp Info
        </button>
        <button 
          onClick={() => setActiveTab('schedule')}
          style={{...styles.tab, ...(activeTab === 'schedule' ? styles.activeTab : {})}}
        >
          <Train size={18} />
          Train Schedule
        </button>
        <button 
          onClick={() => setActiveTab('train')}
          style={{...styles.tab, ...(activeTab === 'train' ? styles.activeTab : {})}}
        >
          <AlertTriangle size={18} />
          Train Changes
        </button>
        <button 
          onClick={() => setActiveTab('team')}
          style={{...styles.tab, ...(activeTab === 'team' ? styles.activeTab : {})}}
        >
          <Users size={18} />
          Team
        </button>
        <button 
          onClick={() => setActiveTab('dates')}
          style={{...styles.tab, ...(activeTab === 'dates' ? styles.activeTab : {})}}
        >
          <Calendar size={18} />
          Event Dates
        </button>
        <button 
          onClick={() => setActiveTab('boxes')}
          style={{...styles.tab, ...(activeTab === 'boxes' ? styles.activeTab : {})}}
        >
          <Box size={18} />
          Content Boxes
        </button>
      </div>

      {/* Content Area */}
      <div style={styles.content}>
        
        {/* Welcome Messages Tab */}
        {activeTab === 'welcome' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Welcome Messages</h2>
              <button onClick={addWelcomeMessage} style={styles.addButton}>
                <Plus size={18} />
                Add Message
              </button>
            </div>
            
            <div style={styles.itemList}>
              {welcomeMessages.map((message, index) => (
                <div key={index} style={styles.item}>
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => updateWelcomeMessage(index, e.target.value)}
                    placeholder="Enter welcome message..."
                    style={styles.input}
                  />
                  <button 
                    onClick={() => removeWelcomeMessage(index)}
                    style={styles.deleteButton}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
              
              {welcomeMessages.length === 0 && (
                <p style={styles.emptyMessage}>No welcome messages yet. Add one!</p>
              )}
            </div>
          </div>
        )}

        {/* Features Tab */}
        {activeTab === 'features' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>App Features</h2>
              <button onClick={addFeature} style={styles.addButton}>
                <Plus size={18} />
                Add Feature
              </button>
            </div>
            
            <div style={styles.itemList}>
              {features.map((feature, index) => (
                <div key={feature.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div style={styles.orderButtons}>
                      <button 
                        onClick={() => moveFeature(index, 'up')}
                        disabled={index === 0}
                        style={styles.orderButton}
                      >
                        <ChevronUp size={18} />
                      </button>
                      <button 
                        onClick={() => moveFeature(index, 'down')}
                        disabled={index === features.length - 1}
                        style={styles.orderButton}
                      >
                        <ChevronDown size={18} />
                      </button>
                    </div>
                    <div style={styles.cardActions}>
                      <button 
                        onClick={() => updateFeature(index, 'isActive', !feature.isActive)}
                        style={styles.toggleButton}
                      >
                        {feature.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                      <button 
                        onClick={() => removeFeature(index)}
                        style={styles.deleteButton}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Title</label>
                    <input
                      type="text"
                      value={feature.title}
                      onChange={(e) => updateFeature(index, 'title', e.target.value)}
                      placeholder="Feature title"
                      style={styles.input}
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Description</label>
                    <textarea
                      value={feature.description}
                      onChange={(e) => updateFeature(index, 'description', e.target.value)}
                      placeholder="Feature description"
                      style={styles.textarea}
                      rows={3}
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Icon Name</label>
                    <input
                      type="text"
                      value={feature.icon}
                      onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                      placeholder="calendar, map, users, etc."
                      style={styles.input}
                    />
                  </div>
                </div>
              ))}
              
              {features.length === 0 && (
                <p style={styles.emptyMessage}>No features yet. Add one!</p>
              )}
            </div>
          </div>
        )}

        {/* News Tab */}
        {activeTab === 'news' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>News & Updates</h2>
              <button onClick={addNews} style={styles.addButton}>
                <Plus size={18} />
                Add News
              </button>
            </div>
            
            <div style={styles.itemList}>
              {news.map((item, index) => (
                <div key={item.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div style={styles.orderButtons}>
                      <button 
                        onClick={() => moveNews(index, 'up')}
                        disabled={index === 0}
                        style={styles.orderButton}
                      >
                        <ChevronUp size={18} />
                      </button>
                      <button 
                        onClick={() => moveNews(index, 'down')}
                        disabled={index === news.length - 1}
                        style={styles.orderButton}
                      >
                        <ChevronDown size={18} />
                      </button>
                    </div>
                    <div style={styles.cardActions}>
                      <button 
                        onClick={() => updateNews(index, 'isActive', !item.isActive)}
                        style={styles.toggleButton}
                      >
                        {item.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                      <button 
                        onClick={() => removeNews(index)}
                        style={styles.deleteButton}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  
                  <div style={styles.formRow}>
                    <div style={{...styles.formGroup, flex: 1}}>
                      <label style={styles.label}>Title</label>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => updateNews(index, 'title', e.target.value)}
                        placeholder="News title"
                        style={styles.input}
                      />
                    </div>
                    
                    <div style={{...styles.formGroup, flex: 1}}>
                      <label style={styles.label}>Subtitle</label>
                      <input
                        type="text"
                        value={item.subtitle}
                        onChange={(e) => updateNews(index, 'subtitle', e.target.value)}
                        placeholder="News subtitle"
                        style={styles.input}
                      />
                    </div>
                  </div>
                  
                  <div style={styles.formRow}>
                    <div style={{...styles.formGroup, flex: 1}}>
                      <MediaUploader
                        key={`news-media-${item.id}-${item.imageUrl || 'empty'}`}
                        token={token}
                        currentUrl={item.imageUrl || item.mediaUrl || ''}
                        label={`News Image/Video (${index + 1})`}
                        type={`news-${item.id || index}`}
                        onUploadSuccess={(url, mediaType) => {
                          const newsId = item.id;
                          console.log(`📸 Upload success for news item (ID: ${newsId}):`, url);
                          // ✅ VERWENDE getStorageUrl STATT getDisplayUrl!
                          const storageUrl = getStorageUrl(url);
                          console.log(`✅ News upload - Storage URL:`, storageUrl);
                          
                          // 🔧 FIX: Verwende ID statt Index für zuverlässige Updates
                          setNews(prevNews => {
                            const updatedNews = prevNews.map(newsItem => {
                              if (newsItem.id === newsId) {
                                console.log(`📰 Updating news ${newsId}: imageUrl = ${storageUrl}`);
                                return {
                                  ...newsItem,
                                  imageUrl: storageUrl,
                                  mediaType: mediaType || 'image'
                                };
                              }
                              return newsItem;
                            });
                            console.log(`📰 News after image update:`, updatedNews.find(n => n.id === newsId));
                            return updatedNews;
                          });
                        }}
                      />
                    </div>
                  </div>
                  
                  <div style={styles.formRow}>
                    <div style={{...styles.formGroup, flex: 1}}>
                      <label style={styles.label}>Date</label>
                      <input
                        type="date"
                        value={item.date ? item.date.split('T')[0] : ''}
                        onChange={(e) => updateNews(index, 'date', e.target.value)}
                        style={styles.input}
                      />
                    </div>
                    
                    <div style={{...styles.formGroup, flex: 1}}>
                      <label style={styles.label}>Scroll Target (Tap Action)</label>
                      <select
                        value={item.scrollTarget || ''}
                        onChange={(e) => {
                          console.log(`📰 News scroll target changed to: ${e.target.value}`);
                          updateNews(index, 'scrollTarget', e.target.value);
                        }}
                        style={styles.select}
                      >
                        <option value="">None (No Action)</option>
                        <option value="nextCamp">🏕️ Next Camp Section</option>
                        <option value="trainSchedule">🚂 Train Schedule</option>
                        <option value="trainChanges">⚠️ Train Changes Alert</option>
                        <option value="supervisors">👥 Supervisors Section</option>
                        <option value="features">⭐ Features Section</option>
                        <option value="contentBoxes">📦 Content Boxes</option>
                      </select>
                      <small style={{fontSize: '12px', color: '#888', marginTop: '5px', display: 'block'}}>
                        Wohin soll gescrollt werden, wenn der User auf diese News tippt?
                      </small>
                    </div>
                  </div>
                </div>
              ))}
              
              {news.length === 0 && (
                <p style={styles.emptyMessage}>No news items yet. Add one!</p>
              )}
            </div>
          </div>
        )}

        {/* Camp Info Tab */}
        {activeTab === 'camp' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>🏕️ Camp Information (Next Camp)</h2>
            </div>
            <p style={styles.sectionSubtitle}>
              Konfiguriere alle Details für das nächste Camp - diese werden in der App angezeigt
            </p>
            
            <div style={styles.card}>
              {/* Basic Camp Info */}
              <div style={styles.formSection}>
                <h3 style={styles.formSectionTitle}>📋 Grundinformationen</h3>
                
                <div style={styles.formRow}>
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Camp Titel *</label>
                    <input
                      type="text"
                      value={campInfo.title}
                      onChange={(e) => updateCampInfo('title', e.target.value)}
                      placeholder="z.B. Welcome Days 2025"
                      style={styles.input}
                    />
                    <small style={styles.helpText}>
                      Dieser Titel erscheint prominent in der App
                    </small>
                  </div>
                  
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Location *</label>
                    <input
                      type="text"
                      value={campInfo.location}
                      onChange={(e) => updateCampInfo('location', e.target.value)}
                      placeholder="z.B. Arosa, Switzerland"
                      style={styles.input}
                    />
                    <small style={styles.helpText}>
                      Standort des Camps
                    </small>
                  </div>
                </div>
                
                <div style={styles.formRow}>
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Theme/Motto</label>
                    <input
                      type="text"
                      value={campInfo.theme}
                      onChange={(e) => updateCampInfo('theme', e.target.value)}
                      placeholder="z.B. Digital Innovation & Team Building"
                      style={styles.input}
                    />
                  </div>
                  
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Kapazität (Teilnehmer)</label>
                    <input
                      type="number"
                      value={campInfo.capacity}
                      onChange={(e) => updateCampInfo('capacity', parseInt(e.target.value) || 0)}
                      placeholder="120"
                      style={styles.input}
                      min="0"
                    />
                  </div>
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Beschreibung *</label>
                  <textarea
                    value={campInfo.description}
                    onChange={(e) => updateCampInfo('description', e.target.value)}
                    placeholder="Beschreibe das Camp... z.B. TOP SECRET - Details will be revealed soon!"
                    rows={4}
                    style={styles.textarea}
                  />
                  <small style={styles.helpText}>
                    Diese Beschreibung wird in der App unterhalb des Camp-Titels angezeigt
                  </small>
                </div>
              </div>
              
              {/* Camp Dates */}
              <div style={styles.formSection}>
                <h3 style={styles.formSectionTitle}>📅 Camp Daten</h3>
                
                <div style={styles.formRow}>
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Start Datum & Zeit *</label>
                    <input
                      type="datetime-local"
                      value={campInfo.startDate ? campInfo.startDate.slice(0, 16) : ''}
                      onChange={(e) => updateCampInfo('startDate', e.target.value)}
                      style={styles.input}
                    />
                  </div>
                  
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>End Datum & Zeit *</label>
                    <input
                      type="datetime-local"
                      value={campInfo.endDate ? campInfo.endDate.slice(0, 16) : ''}
                      onChange={(e) => updateCampInfo('endDate', e.target.value)}
                      style={styles.input}
                    />
                  </div>
                </div>
                
                <div style={styles.infoBox}>
                  <Info size={20} color="#17A2B8" />
                  <p style={styles.infoBoxText}>
                    Diese Daten werden für den Countdown verwendet und auf der Camp-Karte angezeigt
                  </p>
                </div>
              </div>
              
              {/* Header Image */}
              <div style={styles.formSection}>
                <h3 style={styles.formSectionTitle}>🖼️ Header Bild</h3>
                
                <MediaUploader
                  token={token}
                  currentUrl={campInfo.headerImageUrl || ''}
                  label="Camp Header Bild"
                  description="Dieses Bild wird ganz oben auf der Camp-Karte angezeigt (empfohlen: 1200x400px)"
                  type="camp-header"
                  onUploadSuccess={(url) => {
                    // ✅ VERWENDE getStorageUrl!
                    const storageUrl = getStorageUrl(url);
                    console.log(`✅ Camp header upload - Storage URL:`, storageUrl);
                    updateCampInfo('headerImageUrl', storageUrl);
                  }}
                />
                
                {campInfo.headerImageUrl && (
                  <div style={styles.imagePreview}>
                    <img 
                      src={getDisplayUrl(campInfo.headerImageUrl)} 
                      alt="Header Preview" 
                      style={styles.previewImage}
                    />
                    <button 
                      onClick={() => updateCampInfo('headerImageUrl', '')}
                      style={styles.removeImageButton}
                    >
                      <Trash2 size={16} />
                      Bild entfernen
                    </button>
                  </div>
                )}
              </div>
              
              {/* Invitation Image */}
              <div style={styles.formSection}>
                <h3 style={styles.formSectionTitle}>✉️ Einladungs-Bild</h3>
                
                <MediaUploader
                  token={token}
                  currentUrl={campInfo.invitationImageUrl || ''}
                  label="Einladungs-Bild"
                  description="Dieses Bild wird angezeigt, wenn Benutzer auf 'View Invitation' klicken (empfohlen: 800x600px)"
                  type="camp-invitation"
                  onUploadSuccess={(url) => {
                    // ✅ VERWENDE getStorageUrl!
                    const storageUrl = getStorageUrl(url);
                    console.log(`✅ Camp invitation upload - Storage URL:`, storageUrl);
                    updateCampInfo('invitationImageUrl', storageUrl);
                  }}
                />
                
                {campInfo.invitationImageUrl && (
                  <div style={styles.imagePreview}>
                    <img 
                      src={getDisplayUrl(campInfo.invitationImageUrl)} 
                      alt="Invitation Preview" 
                      style={styles.previewImage}
                    />
                    <button 
                      onClick={() => updateCampInfo('invitationImageUrl', '')}
                      style={styles.removeImageButton}
                    >
                      <Trash2 size={16} />
                      Bild entfernen
                    </button>
                  </div>
                )}
              </div>
              
              {/* Train Info - Overview */}
              <div style={styles.formSection}>
                <h3 style={styles.formSectionTitle}>🚂 Zug-Information (Übersicht)</h3>
                
                <div style={styles.warningBox}>
                  <AlertTriangle size={20} color="#FF9800" />
                  <p style={styles.warningBoxText}>
                    <strong>Hinweis:</strong> Die detaillierte Zug-Konfiguration findest du im "Train Schedule" Tab. 
                    Hier kannst du zusätzliche Notizen hinzufügen.
                  </p>
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Zusätzliche Train Informationen / Notizen</label>
                  <textarea
                    value={campInfo.trainNotes || ''}
                    onChange={(e) => updateCampInfo('trainNotes', e.target.value)}
                    placeholder="z.B. Tickets werden im Voraus zugeschickt. Bitte keine Tickets selbst kaufen."
                    rows={3}
                    style={styles.textarea}
                  />
                  <small style={styles.helpText}>
                    Diese Notizen werden unterhalb des Zugfahrplans angezeigt
                  </small>
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>SBB Link (optional)</label>
                  <input
                    type="url"
                    value={campInfo.sbbLink || ''}
                    onChange={(e) => updateCampInfo('sbbLink', e.target.value)}
                    placeholder="https://www.sbb.ch/de/kaufen/pages/fahrplan/fahrplan.xhtml?..."
                    style={styles.input}
                  />
                  <small style={styles.helpText}>
                    Direkter Link zur SBB Fahrplansuche - wird als "View on SBB App" Button angezeigt
                  </small>
                </div>
              </div>
              
              {/* Preview Button */}
              <div style={styles.formSection}>
                <button 
                  onClick={() => {
                    alert('Preview-Funktion kommt bald! Speichere die Änderungen und öffne die App, um das Ergebnis zu sehen.');
                  }}
                  style={styles.previewButton}
                >
                  <Eye size={18} />
                  Vorschau (in App öffnen)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Train Schedule Tab */}
        {activeTab === 'schedule' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>🚂 Zugfahrplan Konfiguration</h2>
            </div>
            <p style={styles.sectionSubtitle}>
              Konfiguriere die Hin- und Rückfahrt zum Camp
            </p>
            
            {/* Quick Setup Helper */}
            <div style={styles.quickSetupBox}>
              <h3 style={styles.quickSetupTitle}>⚡ Quick Setup</h3>
              <p style={styles.quickSetupText}>
                Nutze diese Vorlage für einen schnellen Start:
              </p>
              <button 
                onClick={() => {
                  setTrainSchedule({
                    outbound: {
                      date: '13.08.2025',
                      departureStation: 'Zürich HB',
                      departureTime: '09:12',
                      arrivalStation: 'Arosa',
                      arrivalTime: '12:20',
                      stops: [
                        { station: 'Zürich HB', time: '09:12', platform: 'Gleis 4', type: 'departure', description: 'Departure', train: '' },
                        { station: 'Chur', time: '10:48', platform: 'Gleis 3', type: 'transfer', description: 'Transfer', train: '' },
                        { station: 'Chur', time: '11:17', platform: 'Gleis 5', type: 'departure', description: 'Departure (RhB Extrazug)', train: 'RhB Extrazug' },
                        { station: 'Arosa', time: '12:20', platform: '', type: 'arrival', description: 'Arrival', train: '' }
                      ]
                    },
                    return: {
                      date: '15.08.2025',
                      departureStation: 'Arosa',
                      departureTime: '16:00',
                      arrivalStation: 'Zürich HB',
                      arrivalTime: '19:15',
                      stops: [
                        { station: 'Arosa', time: '16:00', platform: '', type: 'departure', description: 'Departure', train: '' },
                        { station: 'Chur', time: '17:03', platform: 'Gleis 5', type: 'transfer', description: 'Transfer', train: '' },
                        { station: 'Chur', time: '17:12', platform: 'Gleis 3', type: 'departure', description: 'Departure (IR35)', train: 'IR35' },
                        { station: 'Zürich HB', time: '19:15', platform: 'Gleis 4', type: 'arrival', description: 'Arrival', train: '' }
                      ]
                    },
                    additionalInfo: 'Tickets werden im Voraus zugeschickt. Bitte keine Tickets selbst kaufen.'
                  });
                }}
                style={styles.quickSetupButton}
              >
                <Clock size={18} />
                Zürich-Arosa Vorlage laden
              </button>
            </div>
            
            {/* Outbound Journey */}
            <div style={styles.subsection}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.subsectionTitle}>➡️ Hinfahrt zum Camp</h3>
                <button 
                  onClick={() => addTrainStop('outbound')}
                  style={styles.addButton}
                >
                  <Plus size={18} />
                  Halt hinzufügen
                </button>
              </div>
              
              {/* Outbound Overview */}
              <div style={styles.card}>
                <h4 style={styles.overviewTitle}>Übersicht Hinfahrt</h4>
                <div style={styles.formRow}>
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Datum *</label>
                    <input
                      type="text"
                      value={trainSchedule.outbound?.date || ''}
                      onChange={(e) => setTrainSchedule({
                        ...trainSchedule,
                        outbound: { ...trainSchedule.outbound, date: e.target.value }
                      })}
                      placeholder="13.08.2025"
                      style={styles.input}
                    />
                  </div>
                  
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Abfahrtsbahnhof *</label>
                    <input
                      type="text"
                      value={trainSchedule.outbound?.departureStation || ''}
                      onChange={(e) => setTrainSchedule({
                        ...trainSchedule,
                        outbound: { ...trainSchedule.outbound, departureStation: e.target.value }
                      })}
                      placeholder="Zürich HB"
                      style={styles.input}
                    />
                  </div>
                  
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Abfahrtszeit *</label>
                    <input
                      type="time"
                      value={trainSchedule.outbound?.departureTime || ''}
                      onChange={(e) => setTrainSchedule({
                        ...trainSchedule,
                        outbound: { ...trainSchedule.outbound, departureTime: e.target.value }
                      })}
                      style={styles.input}
                    />
                  </div>
                </div>
                
                <div style={styles.formRow}>
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Ankunftsbahnhof *</label>
                    <input
                      type="text"
                      value={trainSchedule.outbound?.arrivalStation || ''}
                      onChange={(e) => setTrainSchedule({
                        ...trainSchedule,
                        outbound: { ...trainSchedule.outbound, arrivalStation: e.target.value }
                      })}
                      placeholder="Arosa"
                      style={styles.input}
                    />
                  </div>
                  
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Ankunftszeit *</label>
                    <input
                      type="time"
                      value={trainSchedule.outbound?.arrivalTime || ''}
                      onChange={(e) => setTrainSchedule({
                        ...trainSchedule,
                        outbound: { ...trainSchedule.outbound, arrivalTime: e.target.value }
                      })}
                      style={styles.input}
                    />
                  </div>
                </div>
              </div>
              
              {/* Outbound Stops */}
              <div style={styles.itemList}>
                <h4 style={styles.stopsTitle}>🚏 Halte & Umstiege</h4>
                
                {(trainSchedule.outbound?.stops || []).map((stop, index) => (
                  <div key={index} style={styles.card}>
                    <div style={styles.cardHeader}>
                      <div style={styles.stopNumber}>#{index + 1}</div>
                      <div style={styles.orderButtons}>
                        <button 
                          onClick={() => moveTrainStop('outbound', index, 'up')}
                          disabled={index === 0}
                          style={styles.orderButton}
                        >
                          <ChevronUp size={18} />
                        </button>
                        <button 
                          onClick={() => moveTrainStop('outbound', index, 'down')}
                          disabled={index === (trainSchedule.outbound?.stops || []).length - 1}
                          style={styles.orderButton}
                        >
                          <ChevronDown size={18} />
                        </button>
                      </div>
                      <button 
                        onClick={() => removeTrainStop('outbound', index)}
                        style={styles.deleteButton}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                    
                    <div style={styles.formRow}>
                      <div style={{...styles.formGroup, flex: 2}}>
                        <label style={styles.label}>Bahnhof *</label>
                        <input
                          type="text"
                          value={stop.station}
                          onChange={(e) => updateTrainStop('outbound', index, 'station', e.target.value)}
                          placeholder="z.B. Zürich HB"
                          style={styles.input}
                        />
                      </div>
                      
                      <div style={{...styles.formGroup, flex: 1}}>
                        <label style={styles.label}>Zeit *</label>
                        <input
                          type="time"
                          value={stop.time}
                          onChange={(e) => updateTrainStop('outbound', index, 'time', e.target.value)}
                          style={styles.input}
                        />
                      </div>
                      
                      <div style={{...styles.formGroup, flex: 1}}>
                        <label style={styles.label}>Gleis</label>
                        <input
                          type="text"
                          value={stop.platform || ''}
                          onChange={(e) => updateTrainStop('outbound', index, 'platform', e.target.value)}
                          placeholder="Gleis 4"
                          style={styles.input}
                        />
                      </div>
                    </div>
                    
                    <div style={styles.formRow}>
                      <div style={{...styles.formGroup, flex: 1}}>
                        <label style={styles.label}>Typ *</label>
                        <select
                          value={stop.type || 'stop'}
                          onChange={(e) => updateTrainStop('outbound', index, 'type', e.target.value)}
                          style={styles.select}
                        >
                          <option value="departure">🟢 Abfahrt</option>
                          <option value="stop">🔵 Zwischenhalt</option>
                          <option value="transfer">🔄 Umstieg</option>
                          <option value="arrival">🔴 Ankunft</option>
                        </select>
                      </div>
                      
                      <div style={{...styles.formGroup, flex: 1}}>
                        <label style={styles.label}>Zug (optional)</label>
                        <input
                          type="text"
                          value={stop.train || ''}
                          onChange={(e) => updateTrainStop('outbound', index, 'train', e.target.value)}
                          placeholder="IR35 / RhB Extrazug"
                          style={styles.input}
                        />
                      </div>
                      
                      <div style={{...styles.formGroup, flex: 1}}>
                        <label style={styles.label}>Beschreibung</label>
                        <input
                          type="text"
                          value={stop.description || ''}
                          onChange={(e) => updateTrainStop('outbound', index, 'description', e.target.value)}
                          placeholder="z.B. Departure"
                          style={styles.input}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                {(!trainSchedule.outbound?.stops || trainSchedule.outbound.stops.length === 0) && (
                  <p style={styles.emptyMessage}>
                    Noch keine Halte hinzugefügt. Klicke auf "Halt hinzufügen", um zu beginnen.
                  </p>
                )}
              </div>
            </div>
            
            {/* Return Journey */}
            <div style={styles.subsection}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.subsectionTitle}>⬅️ Rückfahrt vom Camp</h3>
                <button 
                  onClick={() => addTrainStop('return')}
                  style={styles.addButton}
                >
                  <Plus size={18} />
                  Halt hinzufügen
                </button>
              </div>
              
              {/* Return Overview */}
              <div style={styles.card}>
                <h4 style={styles.overviewTitle}>Übersicht Rückfahrt</h4>
                <div style={styles.formRow}>
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Datum *</label>
                    <input
                      type="text"
                      value={trainSchedule.return?.date || ''}
                      onChange={(e) => setTrainSchedule({
                        ...trainSchedule,
                        return: { ...trainSchedule.return, date: e.target.value }
                      })}
                      placeholder="15.08.2025"
                      style={styles.input}
                    />
                  </div>
                  
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Abfahrtsbahnhof *</label>
                    <input
                      type="text"
                      value={trainSchedule.return?.departureStation || ''}
                      onChange={(e) => setTrainSchedule({
                        ...trainSchedule,
                        return: { ...trainSchedule.return, departureStation: e.target.value }
                      })}
                      placeholder="Arosa"
                      style={styles.input}
                    />
                  </div>
                  
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Abfahrtszeit *</label>
                    <input
                      type="time"
                      value={trainSchedule.return?.departureTime || ''}
                      onChange={(e) => setTrainSchedule({
                        ...trainSchedule,
                        return: { ...trainSchedule.return, departureTime: e.target.value }
                      })}
                      style={styles.input}
                    />
                  </div>
                </div>
                
                <div style={styles.formRow}>
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Ankunftsbahnhof *</label>
                    <input
                      type="text"
                      value={trainSchedule.return?.arrivalStation || ''}
                      onChange={(e) => setTrainSchedule({
                        ...trainSchedule,
                        return: { ...trainSchedule.return, arrivalStation: e.target.value }
                      })}
                      placeholder="Zürich HB"
                      style={styles.input}
                    />
                  </div>
                  
                  <div style={{...styles.formGroup, flex: 1}}>
                    <label style={styles.label}>Ankunftszeit *</label>
                    <input
                      type="time"
                      value={trainSchedule.return?.arrivalTime || ''}
                      onChange={(e) => setTrainSchedule({
                        ...trainSchedule,
                        return: { ...trainSchedule.return, arrivalTime: e.target.value }
                      })}
                      style={styles.input}
                    />
                  </div>
                </div>
              </div>
              
              {/* Return Stops */}
              <div style={styles.itemList}>
                <h4 style={styles.stopsTitle}>🚏 Halte & Umstiege</h4>
                
                {(trainSchedule.return?.stops || []).map((stop, index) => (
                  <div key={index} style={styles.card}>
                    <div style={styles.cardHeader}>
                      <div style={styles.stopNumber}>#{index + 1}</div>
                      <div style={styles.orderButtons}>
                        <button 
                          onClick={() => moveTrainStop('return', index, 'up')}
                          disabled={index === 0}
                          style={styles.orderButton}
                        >
                          <ChevronUp size={18} />
                        </button>
                        <button 
                          onClick={() => moveTrainStop('return', index, 'down')}
                          disabled={index === (trainSchedule.return?.stops || []).length - 1}
                          style={styles.orderButton}
                        >
                          <ChevronDown size={18} />
                        </button>
                      </div>
                      <button 
                        onClick={() => removeTrainStop('return', index)}
                        style={styles.deleteButton}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                    
                    <div style={styles.formRow}>
                      <div style={{...styles.formGroup, flex: 2}}>
                        <label style={styles.label}>Bahnhof *</label>
                        <input
                          type="text"
                          value={stop.station}
                          onChange={(e) => updateTrainStop('return', index, 'station', e.target.value)}
                          placeholder="z.B. Zürich HB"
                          style={styles.input}
                        />
                      </div>
                      
                      <div style={{...styles.formGroup, flex: 1}}>
                        <label style={styles.label}>Zeit *</label>
                        <input
                          type="time"
                          value={stop.time}
                          onChange={(e) => updateTrainStop('return', index, 'time', e.target.value)}
                          style={styles.input}
                        />
                      </div>
                      
                      <div style={{...styles.formGroup, flex: 1}}>
                        <label style={styles.label}>Gleis</label>
                        <input
                          type="text"
                          value={stop.platform || ''}
                          onChange={(e) => updateTrainStop('return', index, 'platform', e.target.value)}
                          placeholder="Gleis 4"
                          style={styles.input}
                        />
                      </div>
                    </div>
                    
                    <div style={styles.formRow}>
                      <div style={{...styles.formGroup, flex: 1}}>
                        <label style={styles.label}>Typ *</label>
                        <select
                          value={stop.type || 'stop'}
                          onChange={(e) => updateTrainStop('return', index, 'type', e.target.value)}
                          style={styles.select}
                        >
                          <option value="departure">🟢 Abfahrt</option>
                          <option value="stop">🔵 Zwischenhalt</option>
                          <option value="transfer">🔄 Umstieg</option>
                          <option value="arrival">🔴 Ankunft</option>
                        </select>
                      </div>
                      
                      <div style={{...styles.formGroup, flex: 1}}>
                        <label style={styles.label}>Zug (optional)</label>
                        <input
                          type="text"
                          value={stop.train || ''}
                          onChange={(e) => updateTrainStop('return', index, 'train', e.target.value)}
                          placeholder="IR35 / RhB Extrazug"
                          style={styles.input}
                        />
                      </div>
                      
                      <div style={{...styles.formGroup, flex: 1}}>
                        <label style={styles.label}>Beschreibung</label>
                        <input
                          type="text"
                          value={stop.description || ''}
                          onChange={(e) => updateTrainStop('return', index, 'description', e.target.value)}
                          placeholder="z.B. Departure"
                          style={styles.input}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                {(!trainSchedule.return?.stops || trainSchedule.return.stops.length === 0) && (
                  <p style={styles.emptyMessage}>
                    Noch keine Halte hinzugefügt. Klicke auf "Halt hinzufügen", um zu beginnen.
                  </p>
                )}
              </div>
            </div>
            
            {/* Additional Info */}
            <div style={styles.formSection}>
              <h3 style={styles.formSectionTitle}>ℹ️ Zusätzliche Informationen</h3>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Wichtige Hinweise für Teilnehmer</label>
                <textarea
                  value={trainSchedule.additionalInfo || ''}
                  onChange={(e) => setTrainSchedule({
                    ...trainSchedule,
                    additionalInfo: e.target.value
                  })}
                  placeholder="z.B. Tickets werden im Voraus zugeschickt. Bitte keine Tickets selbst kaufen."
                  rows={3}
                  style={styles.textarea}
                />
              </div>
            </div>
          </div>
        )}

        {/* Train Changes Tab */}
        {activeTab === 'train' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>🚨 Transport Changes / Schedule Updates</h2>
              <button 
                onClick={() => {
                  setTrainChanges({
                    ...trainChanges,
                    hasChanges: !trainChanges.hasChanges
                  });
                }}
                style={{
                  ...styles.addButton,
                  backgroundColor: trainChanges.hasChanges ? '#28A745' : '#DC3545'
                }}
              >
                {trainChanges.hasChanges ? '✅ Changes Active' : '❌ No Changes'}
              </button>
            </div>
            
            <div style={styles.card}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <input
                    type="checkbox"
                    checked={trainChanges.hasChanges}
                    onChange={(e) => setTrainChanges({...trainChanges, hasChanges: e.target.checked})}
                    style={{marginRight: '8px'}}
                  />
                  <strong>Show Changes Alert Banner in App</strong>
                </label>
                <p style={{fontSize: '14px', color: '#666', marginTop: '5px'}}>
                  Aktiviere dies, um einen Alert Banner in der App anzuzeigen
                </p>
              </div>
              
              {trainChanges.hasChanges && (
                <>
                  {/* Transport Type Selection */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Transport Type / Transportart</label>
                    <select
                      value={trainChanges.transportType || 'train'}
                      onChange={(e) => setTrainChanges({...trainChanges, transportType: e.target.value})}
                      style={styles.select}
                    >
                      <option value="train">🚂 Train / Zug</option>
                      <option value="bus">🚌 Bus</option>
                      <option value="car">🚗 Car / Auto (eigenes Fahrzeug)</option>
                      <option value="carpool">🚗👥 Carpool / Fahrgemeinschaft</option>
                      <option value="flight">✈️ Flight / Flug</option>
                      <option value="other">📍 Other / Sonstiges</option>
                    </select>
                  </div>

                  {/* AUTO-LOAD BUTTON - Train Schedule als Old Schedule laden */}
                  {trainSchedule.outbound && trainSchedule.outbound.stops && trainSchedule.outbound.stops.length > 0 && (
                    <div style={{
                      backgroundColor: '#E3F2FD',
                      border: '2px solid #2196F3',
                      borderRadius: '12px',
                      padding: '15px',
                      marginBottom: '20px'
                    }}>
                      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px'}}>
                        <div>
                          <h4 style={{margin: '0 0 5px 0', color: '#1976D2'}}>
                            🔄 Auto-Load Train Schedule
                          </h4>
                          <p style={{margin: 0, fontSize: '13px', color: '#555'}}>
                            Lade das aktuelle Train Schedule aus dem "Train Schedule" Tab als Old Schedule
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            const stops = trainSchedule.outbound.stops;
                            if (stops && stops.length >= 1) {
                              const firstStop = stops[0];
                              const lastStop = stops[stops.length - 1];
                              
                              let duration = '';
                              if (trainSchedule.outbound.departureTime && trainSchedule.outbound.arrivalTime) {
                                const depTime = trainSchedule.outbound.departureTime;
                                const arrTime = trainSchedule.outbound.arrivalTime;
                                const [depH, depM] = depTime.split(':').map(Number);
                                const [arrH, arrM] = arrTime.split(':').map(Number);
                                const depMinutes = depH * 60 + depM;
                                const arrMinutes = arrH * 60 + arrM;
                                const diffMinutes = arrMinutes - depMinutes;
                                const hours = Math.floor(diffMinutes / 60);
                                const minutes = diffMinutes % 60;
                                duration = `${hours}h ${minutes}min`;
                              }
                              
                              setTrainChanges({
                                ...trainChanges,
                                oldSchedule: {
                                  departure: {
                                    station: firstStop.station || trainSchedule.outbound.departureStation || '',
                                    time: firstStop.time || trainSchedule.outbound.departureTime || ''
                                  },
                                  arrival: {
                                    station: lastStop.station || trainSchedule.outbound.arrivalStation || '',
                                    time: lastStop.time || trainSchedule.outbound.arrivalTime || ''
                                  },
                                  duration: duration || ''
                                }
                              });
                              
                              alert('✅ Train Schedule loaded as Old Schedule!');
                            } else {
                              alert('❌ No valid Train Schedule found. Please configure Train Schedule first.');
                            }
                          }}
                          style={{
                            backgroundColor: '#2196F3',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          📥 Load Current Schedule
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Change Date */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Change Date / Änderungsdatum</label>
                    <input
                      type="date"
                      value={trainChanges.changeDate ? trainChanges.changeDate.split('T')[0] : ''}
                      onChange={(e) => setTrainChanges({...trainChanges, changeDate: e.target.value})}
                      style={styles.input}
                    />
                  </div>
                  
                  {/* OLD SCHEDULE - Simplified */}
                  <div style={styles.subsection}>
                    <h3 style={styles.subsectionTitle}>❌ Old Schedule (Optional)</h3>
                    <p style={{fontSize: '14px', color: '#666', marginBottom: '15px'}}>
                      Wenn du das alte Schedule zeigen möchtest
                    </p>
                    
                    <div style={styles.formRow}>
                      <div style={{...styles.formGroup, flex: 1}}>
                        <label style={styles.label}>Departure Station</label>
                        <input
                          type="text"
                          value={trainChanges.oldSchedule?.departure?.station || ''}
                          onChange={(e) => setTrainChanges({
                            ...trainChanges,
                            oldSchedule: {
                              ...trainChanges.oldSchedule,
                              departure: {
                                ...trainChanges.oldSchedule?.departure,
                                station: e.target.value
                              }
                            }
                          })}
                          placeholder="z.B. Zürich HB"
                          style={styles.input}
                        />
                      </div>
                      
                      <div style={{...styles.formGroup, flex: 1}}>
                        <label style={styles.label}>Departure Time</label>
                        <input
                          type="time"
                          value={trainChanges.oldSchedule?.departure?.time || ''}
                          onChange={(e) => setTrainChanges({
                            ...trainChanges,
                            oldSchedule: {
                              ...trainChanges.oldSchedule,
                              departure: {
                                ...trainChanges.oldSchedule?.departure,
                                time: e.target.value
                              }
                            }
                          })}
                          style={styles.input}
                        />
                      </div>
                    </div>
                    
                    <div style={styles.formRow}>
                      <div style={{...styles.formGroup, flex: 1}}>
                        <label style={styles.label}>Arrival Station</label>
                        <input
                          type="text"
                          value={trainChanges.oldSchedule?.arrival?.station || ''}
                          onChange={(e) => setTrainChanges({
                            ...trainChanges,
                            oldSchedule: {
                              ...trainChanges.oldSchedule,
                              arrival: {
                                ...trainChanges.oldSchedule?.arrival,
                                station: e.target.value
                              }
                            }
                          })}
                          placeholder="z.B. Arosa"
                          style={styles.input}
                        />
                      </div>
                      
                      <div style={{...styles.formGroup, flex: 1}}>
                        <label style={styles.label}>Arrival Time</label>
                        <input
                          type="time"
                          value={trainChanges.oldSchedule?.arrival?.time || ''}
                          onChange={(e) => setTrainChanges({
                            ...trainChanges,
                            oldSchedule: {
                              ...trainChanges.oldSchedule,
                              arrival: {
                                ...trainChanges.oldSchedule?.arrival,
                                time: e.target.value
                              }
                            }
                          })}
                          style={styles.input}
                        />
                      </div>
                    </div>
                    
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Duration (optional)</label>
                      <input
                        type="text"
                        value={trainChanges.oldSchedule?.duration || ''}
                        onChange={(e) => setTrainChanges({
                          ...trainChanges,
                          oldSchedule: {
                            ...trainChanges.oldSchedule,
                            duration: e.target.value
                          }
                        })}
                        placeholder="z.B. 3h 08min"
                        style={styles.input}
                      />
                    </div>
                  </div>
                  
                  {/* NEW SCHEDULE - Stops Management */}
                  <div style={styles.subsection}>
                    <div style={styles.sectionHeader}>
                      <h3 style={styles.subsectionTitle}>✅ New Schedule</h3>
                      <button 
                        onClick={() => {
                          const newStop = {
                            id: Date.now().toString(),
                            station: '',
                            time: '',
                            train: '',
                            type: 'stop'
                          };
                          setTrainChanges({
                            ...trainChanges,
                            newSchedule: {
                              ...trainChanges.newSchedule,
                              stops: [...(trainChanges.newSchedule?.stops || []), newStop]
                            }
                          });
                        }}
                        style={styles.addButton}
                      >
                        <Plus size={18} />
                        Add Stop
                      </button>
                    </div>
                    
                    <div style={styles.itemList}>
                      {(trainChanges.newSchedule?.stops || []).map((stop, index) => (
                        <div key={stop.id || index} style={styles.card}>
                          <div style={styles.cardHeader}>
                            <div style={styles.stopNumber}>{index + 1}</div>
                            <div style={{display: 'flex', gap: '5px'}}>
                              {index > 0 && (
                                <button 
                                  onClick={() => {
                                    const stops = [...trainChanges.newSchedule.stops];
                                    [stops[index], stops[index-1]] = [stops[index-1], stops[index]];
                                    setTrainChanges({
                                      ...trainChanges,
                                      newSchedule: {
                                        ...trainChanges.newSchedule,
                                        stops
                                      }
                                    });
                                  }}
                                  style={styles.orderButton}
                                  title="Move Up"
                                >
                                  <ChevronUp size={18} />
                                </button>
                              )}
                              {index < (trainChanges.newSchedule?.stops || []).length - 1 && (
                                <button 
                                  onClick={() => {
                                    const stops = [...trainChanges.newSchedule.stops];
                                    [stops[index], stops[index+1]] = [stops[index+1], stops[index]];
                                    setTrainChanges({
                                      ...trainChanges,
                                      newSchedule: {
                                        ...trainChanges.newSchedule,
                                        stops
                                      }
                                    });
                                  }}
                                  style={styles.orderButton}
                                  title="Move Down"
                                >
                                  <ChevronDown size={18} />
                                </button>
                              )}
                              <button 
                                onClick={() => {
                                  setTrainChanges({
                                    ...trainChanges,
                                    newSchedule: {
                                      ...trainChanges.newSchedule,
                                      stops: trainChanges.newSchedule.stops.filter((_, i) => i !== index)
                                    }
                                  });
                                }}
                                style={styles.deleteButton}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                          
                          <div style={styles.formRow}>
                            <div style={{...styles.formGroup, flex: 2}}>
                              <label style={styles.label}>Station / Stop</label>
                              <input
                                type="text"
                                value={stop.station}
                                onChange={(e) => {
                                  const stops = [...trainChanges.newSchedule.stops];
                                  stops[index].station = e.target.value;
                                  setTrainChanges({
                                    ...trainChanges,
                                    newSchedule: {
                                      ...trainChanges.newSchedule,
                                      stops
                                    }
                                  });
                                }}
                                placeholder="z.B. Zürich HB"
                                style={styles.input}
                              />
                            </div>
                            
                            <div style={{...styles.formGroup, flex: 1}}>
                              <label style={styles.label}>Time</label>
                              <input
                                type="time"
                                value={stop.time}
                                onChange={(e) => {
                                  const stops = [...trainChanges.newSchedule.stops];
                                  stops[index].time = e.target.value;
                                  setTrainChanges({
                                    ...trainChanges,
                                    newSchedule: {
                                      ...trainChanges.newSchedule,
                                      stops
                                    }
                                  });
                                }}
                                style={styles.input}
                              />
                            </div>
                            
                            <div style={{...styles.formGroup, flex: 1}}>
                              <label style={styles.label}>Type</label>
                              <select
                                value={stop.type || 'stop'}
                                onChange={(e) => {
                                  const stops = [...trainChanges.newSchedule.stops];
                                  stops[index].type = e.target.value;
                                  setTrainChanges({
                                    ...trainChanges,
                                    newSchedule: {
                                      ...trainChanges.newSchedule,
                                      stops
                                    }
                                  });
                                }}
                                style={styles.select}
                              >
                                <option value="departure">🚀 Departure</option>
                                <option value="stop">🛑 Stop</option>
                                <option value="transfer">🔄 Transfer</option>
                                <option value="arrival">✅ Arrival</option>
                              </select>
                            </div>
                          </div>
                          
                          <div style={styles.formGroup}>
                            <label style={styles.label}>Train/Bus Number (optional)</label>
                            <input
                              type="text"
                              value={stop.train || ''}
                              onChange={(e) => {
                                const stops = [...trainChanges.newSchedule.stops];
                                stops[index].train = e.target.value;
                                setTrainChanges({
                                  ...trainChanges,
                                  newSchedule: {
                                    ...trainChanges.newSchedule,
                                    stops
                                  }
                                });
                              }}
                              placeholder="z.B. IR35, Bus 321"
                              style={styles.input}
                            />
                          </div>
                        </div>
                      ))}
                      
                      {(!trainChanges.newSchedule?.stops || trainChanges.newSchedule.stops.length === 0) && (
                        <p style={styles.emptyMessage}>No stops yet. Click "Add Stop" to begin!</p>
                      )}
                    </div>
                    
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Total Duration</label>
                      <input
                        type="text"
                        value={trainChanges.newSchedule?.totalDuration || ''}
                        onChange={(e) => setTrainChanges({
                          ...trainChanges,
                          newSchedule: {
                            ...trainChanges.newSchedule,
                            totalDuration: e.target.value
                          }
                        })}
                        placeholder="z.B. 3h 45min"
                        style={styles.input}
                      />
                    </div>
                  </div>
                  
                  {/* Important Notes */}
                  <div style={styles.formSection}>
                    <label style={styles.label}>Important Notes / Wichtige Hinweise</label>
                    <p style={{fontSize: '13px', color: '#888', marginBottom: '8px'}}>
                      Füge wichtige Informationen hinzu (z.B. "Bitte 15 Minuten früher am Bahnhof sein")
                    </p>
                    {(trainChanges.importantNotes || []).map((note, index) => (
                      <div key={index} style={styles.item}>
                        <input
                          type="text"
                          value={note}
                          onChange={(e) => {
                            const notes = [...trainChanges.importantNotes];
                            notes[index] = e.target.value;
                            setTrainChanges({...trainChanges, importantNotes: notes});
                          }}
                          placeholder="Important note..."
                          style={styles.input}
                        />
                        <button 
                          onClick={() => {
                            setTrainChanges({
                              ...trainChanges,
                              importantNotes: trainChanges.importantNotes.filter((_, i) => i !== index)
                            });
                          }}
                          style={styles.deleteButton}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                    <button 
                      onClick={() => {
                        setTrainChanges({
                          ...trainChanges,
                          importantNotes: [...(trainChanges.importantNotes || []), '']
                        });
                      }}
                      style={styles.addButton}
                    >
                      <Plus size={18} />
                      Add Note
                    </button>
                  </div>
                </>
              )}
            </div>
            
            {/* Preview Box */}
            {trainChanges.hasChanges && (
              <div style={{
                backgroundColor: '#FFF3CD',
                border: '2px solid #FF9500',
                borderRadius: '12px',
                padding: '20px',
                marginTop: '20px'
              }}>
                <h3 style={{margin: '0 0 15px 0', color: '#856404'}}>
                  📱 App Preview
                </h3>
                <p style={{fontSize: '14px', color: '#856404', marginBottom: '10px'}}>
                  So wird der Alert Banner in der App angezeigt:
                </p>
                <div style={{
                  backgroundColor: '#EC0000',
                  color: 'white',
                  padding: '15px',
                  borderRadius: '8px'
                }}>
                  <div style={{display: 'flex', alignItems: 'center', marginBottom: '8px'}}>
                    <strong style={{fontSize: '16px'}}>⚠️ New Changes</strong>
                    <span style={{
                      marginLeft: '10px',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      color: '#EC0000',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}>
                      UPDATED
                    </span>
                  </div>
                  <p style={{margin: 0, fontSize: '14px'}}>
                    {trainChanges.transportType === 'train' ? 'Train' :
                     trainChanges.transportType === 'bus' ? 'Bus' :
                     trainChanges.transportType === 'car' ? 'Travel' :
                     trainChanges.transportType === 'carpool' ? 'Carpool' :
                     trainChanges.transportType === 'flight' ? 'Flight' :
                     'Schedule'} schedule updated - Tap to view details
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Team Tab */}
        {activeTab === 'team' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Team Management</h2>
            
            {/* Supervisors */}
            <div style={styles.subsection}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.subsectionTitle}>Supervisors</h3>
                <button onClick={() => addTeamMember('supervisor')} style={styles.addButton}>
                  <Plus size={18} />
                  Add Supervisor
                </button>
              </div>
              
              {supervisors.map((member, index) => (
                <div key={member.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div style={styles.cardActions}>
                      <button 
                        onClick={() => removeTeamMember('supervisor', index)}
                        style={styles.deleteButton}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  
                  <div style={styles.formRow}>
                    <div style={{...styles.formGroup, flex: 1}}>
                      <label style={styles.label}>Name</label>
                      <input
                        type="text"
                        value={member.name}
                        onChange={(e) => updateTeamMember('supervisor', index, 'name', e.target.value)}
                        style={styles.input}
                      />
                    </div>
                    
                    <div style={{...styles.formGroup, flex: 1}}>
                      <label style={styles.label}>Title</label>
                      <input
                        type="text"
                        value={member.title}
                        onChange={(e) => updateTeamMember('supervisor', index, 'title', e.target.value)}
                        style={styles.input}
                      />
                    </div>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <MediaUploader
                      token={token}
                      currentUrl={member.imageUrl || ''}
                      label="Profile Image"
                      type="team-supervisor"
                      onUploadSuccess={(url) => {
                        // ✅ VERWENDE getStorageUrl!
                        const storageUrl = getStorageUrl(url);
                        updateTeamMember('supervisor', index, 'imageUrl', storageUrl);
                      }}
                    />
                  </div>
                  
                  <div style={styles.formRow}>
                    <div style={{...styles.formGroup, flex: 1}}>
                      <label style={styles.label}>Phone</label>
                      <input
                        type="text"
                        value={member.phone}
                        onChange={(e) => updateTeamMember('supervisor', index, 'phone', e.target.value)}
                        style={styles.input}
                      />
                    </div>
                    
                    <div style={{...styles.formGroup, flex: 1}}>
                      <label style={styles.label}>Email</label>
                      <input
                        type="email"
                        value={member.email}
                        onChange={(e) => updateTeamMember('supervisor', index, 'email', e.target.value)}
                        style={styles.input}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Development Team */}
            <div style={styles.subsection}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.subsectionTitle}>Development Team</h3>
                <button onClick={() => addTeamMember('developer')} style={styles.addButton}>
                  <Plus size={18} />
                  Add Developer
                </button>
              </div>
              
              {developmentTeam.map((member, index) => (
                <div key={member.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div style={styles.cardActions}>
                      <button 
                        onClick={() => removeTeamMember('developer', index)}
                        style={styles.deleteButton}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  
                  <div style={styles.formRow}>
                    <div style={{...styles.formGroup, flex: 1}}>
                      <label style={styles.label}>Name</label>
                      <input
                        type="text"
                        value={member.name}
                        onChange={(e) => updateTeamMember('developer', index, 'name', e.target.value)}
                        style={styles.input}
                      />
                    </div>
                    
                    <div style={{...styles.formGroup, flex: 1}}>
                      <label style={styles.label}>Role</label>
                      <input
                        type="text"
                        value={member.title}
                        onChange={(e) => updateTeamMember('developer', index, 'title', e.target.value)}
                        style={styles.input}
                      />
                    </div>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <MediaUploader
                      token={token}
                      currentUrl={member.imageUrl || ''}
                      label="Profile Image"
                      type="team-developer"
                      onUploadSuccess={(url) => {
                        // ✅ VERWENDE getStorageUrl!
                        const storageUrl = getStorageUrl(url);
                        updateTeamMember('developer', index, 'imageUrl', storageUrl);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            {/* Special Thanks */}
            <div style={styles.subsection}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.subsectionTitle}>Special Thanks</h3>
                <button onClick={() => addTeamMember('special_thanks')} style={styles.addButton}>
                  <Plus size={18} />
                  Add Person
                </button>
              </div>
              
              {specialThanks.map((member, index) => (
                <div key={member.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div style={styles.cardActions}>
                      <button 
                        onClick={() => removeTeamMember('special_thanks', index)}
                        style={styles.deleteButton}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  
                  <div style={styles.formRow}>
                    <div style={{...styles.formGroup, flex: 1}}>
                      <label style={styles.label}>Name</label>
                      <input
                        type="text"
                        value={member.name}
                        onChange={(e) => updateTeamMember('special_thanks', index, 'name', e.target.value)}
                        style={styles.input}
                      />
                    </div>
                    
                    <div style={{...styles.formGroup, flex: 1}}>
                      <label style={styles.label}>Contribution</label>
                      <input
                        type="text"
                        value={member.title}
                        onChange={(e) => updateTeamMember('special_thanks', index, 'title', e.target.value)}
                        style={styles.input}
                      />
                    </div>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <MediaUploader
                      token={token}
                      currentUrl={member.imageUrl || ''}
                      label="Profile Image"
                      type="team-special-thanks"
                      onUploadSuccess={(url) => {
                        // ✅ VERWENDE getStorageUrl!
                        const storageUrl = getStorageUrl(url);
                        updateTeamMember('special_thanks', index, 'imageUrl', storageUrl);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Event Dates Tab */}
        {activeTab === 'dates' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Event Dates</h2>
            
            <div style={styles.card}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Event Start Date</label>
                <input
                  type="datetime-local"
                  value={eventDates.startDate ? eventDates.startDate.slice(0, 16) : ''}
                  onChange={(e) => setEventDates({...eventDates, startDate: e.target.value})}
                  style={styles.input}
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Event End Date</label>
                <input
                  type="datetime-local"
                  value={eventDates.endDate ? eventDates.endDate.slice(0, 16) : ''}
                  onChange={(e) => setEventDates({...eventDates, endDate: e.target.value})}
                  style={styles.input}
                />
              </div>
            </div>
          </div>
        )}

        {/* Content Boxes Tab */}
        {activeTab === 'boxes' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Content Boxes</h2>
              <button onClick={addContentBox} style={styles.addButton}>
                <Plus size={18} />
                Add Box
              </button>
            </div>
            
            <div style={styles.itemList}>
              {contentBoxes.map((box, index) => (
                <div key={box.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div style={styles.orderButtons}>
                      <button 
                        onClick={() => moveContentBox(index, 'up')}
                        disabled={index === 0}
                        style={styles.orderButton}
                      >
                        <ChevronUp size={18} />
                      </button>
                      <button 
                        onClick={() => moveContentBox(index, 'down')}
                        disabled={index === contentBoxes.length - 1}
                        style={styles.orderButton}
                      >
                        <ChevronDown size={18} />
                      </button>
                    </div>
                    <div style={styles.cardActions}>
                      <button 
                        onClick={() => updateContentBox(index, 'isActive', !box.isActive)}
                        style={styles.toggleButton}
                      >
                        {box.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                      <button 
                        onClick={() => removeContentBox(index)}
                        style={styles.deleteButton}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  
                  <div style={styles.formRow}>
                    <div style={{...styles.formGroup, flex: 1}}>
                      <label style={styles.label}>Type</label>
                      <select
                        value={box.boxType || 'info'}
                        onChange={(e) => {
                          console.log(`🔄 Box type changed to: ${e.target.value}`);
                          updateContentBox(index, 'boxType', e.target.value);
                        }}
                        style={styles.select}
                      >
                        <option value="info">Info</option>
                        <option value="warning">Warning</option>
                        <option value="success">Success</option>
                        <option value="error">Error</option>
                      </select>
                    </div>
                    
                    <div style={{...styles.formGroup, flex: 1}}>
                      <label style={styles.label}>Icon</label>
                      <input
                        type="text"
                        value={box.icon}
                        onChange={(e) => updateContentBox(index, 'icon', e.target.value)}
                        placeholder="info, warning, etc."
                        style={styles.input}
                      />
                    </div>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Title</label>
                    <input
                      type="text"
                      value={box.title}
                      onChange={(e) => updateContentBox(index, 'title', e.target.value)}
                      placeholder="Box title"
                      style={styles.input}
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Content</label>
                    <textarea
                      value={box.content}
                      onChange={(e) => updateContentBox(index, 'content', e.target.value)}
                      placeholder="Box content..."
                      style={styles.textarea}
                      rows={4}
                    />
                  </div>
                </div>
              ))}
              
              {contentBoxes.length === 0 && (
                <p style={styles.emptyMessage}>No content boxes yet. Add one!</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Styles
const styles = {
  container: {
    padding: '20px',
    maxWidth: '1400px',
    margin: '0 auto',
    backgroundColor: '#f5f5f5',
    minHeight: '100vh'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    gap: '20px'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #da291c',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    marginTop: '5px'
  },
  backupInfo: {
    fontSize: '12px',
    color: '#4CAF50',
    marginTop: '5px',
    fontStyle: 'italic'
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 24px',
    backgroundColor: '#da291c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s'
  },
  successAlert: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '15px',
    backgroundColor: '#d4edda',
    color: '#155724',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '1px solid #c3e6cb'
  },
  errorAlert: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '15px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '1px solid #f5c6cb'
  },
  tabContainer: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    backgroundColor: 'white',
    padding: '15px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflowX: 'auto'
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s',
    whiteSpace: 'nowrap'
  },
  activeTab: {
    backgroundColor: '#da291c',
    color: 'white'
  },
  content: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '30px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    minHeight: '500px'
  },
  section: {
    marginBottom: '30px'
  },
  subsection: {
    marginTop: '30px',
    paddingTop: '20px',
    borderTop: '1px solid #e0e0e0'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0
  },
  subsectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#555',
    margin: 0
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#da291c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s'
  },
  itemList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  item: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center'
  },
  card: {
    backgroundColor: '#f9f9f9',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '15px'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px'
  },
  orderButtons: {
    display: 'flex',
    gap: '5px'
  },
  orderButton: {
    padding: '5px',
    backgroundColor: '#f0f0f0',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  cardActions: {
    display: 'flex',
    gap: '10px'
  },
  toggleButton: {
    padding: '8px',
    backgroundColor: '#f0f0f0',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center'
  },
  deleteButton: {
    padding: '8px',
    backgroundColor: '#fee',
    color: '#c00',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center'
  },
  formGroup: {
    marginBottom: '15px'
  },
  formRow: {
    display: 'flex',
    gap: '15px',
    marginBottom: '15px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#555',
    marginBottom: '8px'
  },
  input: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'monospace',
    boxSizing: 'border-box',
    resize: 'vertical'
  },
  select: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: 'white'
  },
  emptyMessage: {
    textAlign: 'center',
    color: '#999',
    fontSize: '14px',
    padding: '40px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px'
  },
  // New styles for enhanced Camp Info and Train Schedule tabs
  sectionSubtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '20px',
    fontStyle: 'italic'
  },
  formSection: {
    marginBottom: '30px',
    paddingBottom: '30px',
    borderBottom: '1px solid #e0e0e0'
  },
  formSectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  helpText: {
    fontSize: '12px',
    color: '#888',
    marginTop: '5px',
    display: 'block'
  },
  infoBox: {
    display: 'flex',
    gap: '10px',
    backgroundColor: '#E1F5FE',
    border: '1px solid #81D4FA',
    borderRadius: '8px',
    padding: '12px',
    marginTop: '10px',
    alignItems: 'flex-start'
  },
  infoBoxText: {
    fontSize: '13px',
    color: '#0277BD',
    margin: 0,
    lineHeight: '1.5'
  },
  warningBox: {
    display: 'flex',
    gap: '10px',
    backgroundColor: '#FFF3E0',
    border: '1px solid #FFB74D',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '15px',
    alignItems: 'flex-start'
  },
  warningBoxText: {
    fontSize: '13px',
    color: '#E65100',
    margin: 0,
    lineHeight: '1.5'
  },
  imagePreview: {
    marginTop: '15px',
    position: 'relative'
  },
  previewImage: {
    width: '100%',
    maxWidth: '600px',
    height: 'auto',
    borderRadius: '8px',
    border: '2px solid #e0e0e0'
  },
  removeImageButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    marginTop: '10px',
    padding: '8px 16px',
    backgroundColor: '#fee',
    color: '#c00',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  previewButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#3D3935',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    margin: '20px auto',
    transition: 'all 0.3s'
  },
  quickSetupBox: {
    backgroundColor: '#FFF3E0',
    border: '2px solid #FFB74D',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '30px'
  },
  quickSetupTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#E65100',
    marginBottom: '10px',
    margin: 0
  },
  quickSetupText: {
    fontSize: '14px',
    color: '#555',
    marginBottom: '15px',
    marginTop: '10px'
  },
  quickSetupButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#FF9800',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  },
  stopNumber: {
    width: '32px',
    height: '32px',
    borderRadius: '16px',
    backgroundColor: '#EC0000',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold'
  },
  stopsTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#555',
    marginBottom: '15px',
    marginTop: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  overviewTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '15px',
    marginTop: 0
  }
};

export default EarlyAccessContentManagement;
